import { CUSTOM_ELEMENTS_SCHEMA, Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, take } from 'rxjs';
import {
  InstitutoPrevidenciarioAvaliacao,
  InstitutoPrevidenciarioDetalhe,
  InstitutoPrevidenciarioDocumento,
} from '../../../core/models/instituto-previdenciario.model';
import { AvaliacaoDocumentoRequestDto } from '../../../core/api/models';
import { InstitutoPrevidenciarioService } from '../../../core/services/instituto-previdenciario.service';
import { ToastService } from '../../../core/services/toast.service';
import { INSTITUTO_PLANOS, getInstitutoOpcaoLabel, normalizarStatus } from '../../../core/constants/instituto-previdenciario-opcoes';
import { formatDateIsoToBr } from '../../../core/utils/date-format.util';
import { VivestModalAreaVisivelDirective } from '../../../shared/directives/modal-vivest/vivest-modal-area-visivel.directive';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { InstitutoStatusTagComponent } from '../../../shared/components/instituto-status-tag/instituto-status-tag.component';

/** Limites do comentário enviado ao participante junto com o pedido de reenvio. */
export const COMENTARIO_MINIMO = 5;
export const COMENTARIO_MAXIMO = 250;

/** Label de `INSTITUTO_STATUS` em que a seção de documentos não aparece. */
const STATUS_EM_RETENCAO = 'Em retenção';

/** `statusAvaliacao` do documento que o participante reenviou e aguarda nova análise. */
const DOCUMENTO_REENVIADO = 'reenviado';

/**
 * `statusAvaliacao` (e `resultado` da avaliação) do documento rejeitado pelo administrador; a API
 * do Instituto manda "REPROVADO". Comparados por `normalizarStatus`.
 */
const DOCUMENTO_REJEITADO = ['reprovado', 'rejeitado'];


/** Texto exibido para cada código de status da API; um código fora daqui aparece como veio. */
const STATUS_DETALHE_LABEL: Record<string, string> = {
  AGUARDANDO_ESCOLHA_INSTITUTO: 'Aguardando escolha do instituto',
  AGUARDANDO_VALIDACAO_DOCUMENTOS: 'Aguardando validação documentos',
  AGUARDANDO_ASSINATURA_TERMO: 'Aguardando assinatura termo',
  AGUARDANDO_REVISAO: 'Aguardando revisão',
  AGUARDANDO_ENVIO_DOCUMENTOS: 'Aguardando envio documentos',
  AGUARDANDO_TIPO_IR: 'Aguardando tipo IR',
  AGUARDANDO_DADOS_CADASTRAIS: 'Aguardando dados cadastrais',
  AGUARDANDO_REENVIO_DOCUMENTOS: 'Aguardando reenvio documentos',
  FINALIZADO: 'Concluído',
  CANCELADO: 'Cancelado',
};

@Component({
  selector: 'app-detalhe-solicitacao',
  imports: [ReactiveFormsModule, VivestModalAreaVisivelDirective, InstitutoStatusTagComponent],
  templateUrl: './detalhe-solicitacao.component.html',
  styleUrl: './detalhe-solicitacao.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class DetalheSolicitacaoComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly institutoPrevidenciarioService = inject(InstitutoPrevidenciarioService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly sanitizer = inject(DomSanitizer);

  private readonly solicitacaoSignal = signal<InstitutoPrevidenciarioDetalhe | null>(null);
  readonly solicitacao = this.solicitacaoSignal.asReadonly();
  readonly documentos = computed(() => this.solicitacao()?.docs ?? []);
  /**
   * Mudanças de status da mais recente para a mais antiga (data ISO compara como texto). Só entram
   * as que têm `statusUnificado`; as que a API manda com ele `null` ficam de fora.
   */
  readonly historico = computed(() =>
    (this.solicitacao()?.historico ?? [])
      .filter((item) => !!item.statusUnificado)
      .sort((a, b) => (b.alteradoEm ?? '').localeCompare(a.alteradoEm ?? '')),
  );
  /** Em "Em retenção" o participante ainda não enviou documentos: a seção não aparece. */
  readonly mostrarDocumentos = computed(
    () => normalizarStatus(this.solicitacao()?.status) !== normalizarStatus(STATUS_EM_RETENCAO),
  );
  /** Aprovar ou pedir reenvio só quando a API devolve `podeAvaliarDocs`; senão, só consulta. */
  readonly podeValidarDocumentos = computed(() => this.solicitacao()?.podeAvaliarDocs === true);
  /** Documentos que o participante reenviou e aguardam nova análise. */
  readonly documentosReenviados = computed(() => this.documentos().filter((documento) => this.reenviado(documento)));
  /** "Comprovante de residência" ou "RG, CPF e Comprovante de residência". */
  readonly nomesReenviados = computed(() => {
    const nomes = this.documentosReenviados().map((documento) => documento.tipoDocumento);
    return nomes.length > 1 ? `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}` : (nomes[0] ?? '');
  });
  /** Participante com um documento só para validar: os textos das ações ficam no singular. */
  readonly documentoUnico = computed(() => this.documentos().length === 1);
  readonly naoEncontrada = signal(false);
  readonly formatDate = formatDateIsoToBr;
  /**
   * Texto do status (topo e histórico), pelo de-para de `STATUS_DETALHE_LABEL`; a cor da
   * `app-instituto-status-tag` sai desse texto (`getInstitutoStatusCor`).
   */
  readonly statusDetalheLabel = (status: string | null | undefined): string =>
    STATUS_DETALHE_LABEL[(status ?? '').trim().toUpperCase()] ?? status ?? '';
  /**
   * Status do topo: o `statusUnificado` (já com a label de `INSTITUTO_STATUS`) da mudança mais
   * recente do histórico; sem histórico, o de-para do `status` da solicitação.
   */
  readonly statusAtual = computed(
    () => this.historico()[0]?.statusUnificado || this.statusDetalheLabel(this.solicitacao()?.status),
  );
  readonly planoLabel = (id: number | null) => getInstitutoOpcaoLabel(INSTITUTO_PLANOS, id) ?? '—';

  readonly confirmacaoAberta = signal(false);
  readonly reenvioAberto = signal(false);
  readonly comentarioMaximo = COMENTARIO_MAXIMO;

  /** Documento aberto no modal de visualização; `null` com o modal fechado. */
  readonly documentoVisualizado = signal<InstitutoPrevidenciarioDocumento | null>(null);
  /**
   * O link vem da API e vai para o `src` do iframe, que exige URL confiável. Sem fragmento próprio,
   * ganha `#view=FitH`: o visualizador de PDF abre a página na largura do modal.
   */
  readonly urlVisualizacao = computed<SafeResourceUrl | null>(() => {
    const documento = this.documentoVisualizado();
    if (!documento) {
      return null;
    }
    const url = documento.link.includes('#') ? documento.link : `${documento.link}#view=FitH`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  });

  /**
   * Pedido de reenvio: ao menos um documento (pelo `idDocumento`) e o comentário, obrigatório —
   * vai para a API como motivo da reprovação. Só espaços não conta como comentário.
   */
  readonly reenvioForm = this.fb.nonNullable.group({
    documentos: [[] as number[], Validators.required],
    comentarios: [
      '',
      [
        Validators.required,
        Validators.pattern(/\S/),
        Validators.minLength(COMENTARIO_MINIMO),
        Validators.maxLength(COMENTARIO_MAXIMO),
      ],
    ],
  });

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');

    if (!id) {
      this.naoEncontrada.set(true);
      return;
    }

    this.carregarSolicitacao(id);
  }

  /** `2026-10-01T09:15:00` → `01/10/2026 às 09:15`; sem hora, só a data. */
  dataHora(iso: string): string {
    const [data, hora = ''] = iso.split(/[T ]/);
    const dataBr = formatDateIsoToBr(data);
    return hora ? `${dataBr} às ${hora.slice(0, 5)}` : dataBr;
  }

  reenviado(documento: InstitutoPrevidenciarioDocumento): boolean {
    return normalizarStatus(documento.statusAvaliacao) === DOCUMENTO_REENVIADO;
  }

  rejeitado(documento: InstitutoPrevidenciarioDocumento): boolean {
    return DOCUMENTO_REJEITADO.includes(normalizarStatus(documento.statusAvaliacao));
  }

  /** Avaliação mais recente que rejeitou o documento (motivo, quando e por quem). */
  rejeicao(documento: InstitutoPrevidenciarioDocumento): InstitutoPrevidenciarioAvaliacao | undefined {
    return [...(documento.avaliacoes ?? [])]
      .filter((avaliacao) => DOCUMENTO_REJEITADO.includes(normalizarStatus(avaliacao.resultado)))
      .sort((a, b) => b.avaliadoEm.localeCompare(a.avaliadoEm))[0];
  }

  voltar(): void {
    void this.router.navigate(['/instituto/instituto-previdenciario']);
  }

  // ── visualização do documento ───────────────────────────────────────────────

  abrirVisualizacao(documento: InstitutoPrevidenciarioDocumento): void {
    this.documentoVisualizado.set(documento);
  }

  fecharVisualizacao(): void {
    this.documentoVisualizado.set(null);
  }

  // ── aprovação ───────────────────────────────────────────────────────────────

  abrirConfirmacao(): void {
    this.confirmacaoAberta.set(true);
  }

  fecharConfirmacao(): void {
    this.confirmacaoAberta.set(false);
  }

  /**
   * Aprova todos os documentos da solicitação numa chamada só (cada um como `APROVADO`) e recarrega
   * a solicitação. Como no reenvio, o modal fecha assim que o envio começa.
   */
  aprovar(): void {
    const idSolicitacao = this.route.snapshot.paramMap.get('id');
    const documentos = this.documentos();
    this.confirmacaoAberta.set(false);

    if (!idSolicitacao || !documentos.length) {
      return;
    }

    const unico = this.documentoUnico();
    const aprovados: AvaliacaoDocumentoRequestDto[] = documentos.map(({ idDocumento }) => ({
      idDocumento,
      resultado: 'APROVADO',
    }));
    this.loaderService.showLoader();

    this.institutoPrevidenciarioService
      .avaliarDocumentos(idSolicitacao, aprovados)
      .pipe(
        take(1),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: () => {
          this.toastService.show(unico ? 'Documento aprovado.' : 'Documentos aprovados.', 'success');
          this.carregarSolicitacao(idSolicitacao);
        },
        error: () =>
          this.toastService.show(
            unico ? 'Não foi possível aprovar o documento.' : 'Não foi possível aprovar os documentos.',
            'error',
          ),
      });
  }

  // ── pedido de reenvio ───────────────────────────────────────────────────────

  /** Com um documento só, ele já vem marcado: não há o que escolher. */
  abrirReenvio(): void {
    const documentos = this.documentos();
    this.reenvioForm.reset({ documentos: this.documentoUnico() ? [documentos[0].idDocumento] : [] });
    this.reenvioAberto.set(true);
  }

  fecharReenvio(): void {
    this.reenvioAberto.set(false);
  }

  documentoSelecionado(idDocumento: number): boolean {
    return this.reenvioForm.controls.documentos.value.includes(idDocumento);
  }

  alternarDocumento(idDocumento: number, marcado: boolean): void {
    const controle = this.reenvioForm.controls.documentos;
    const outros = controle.value.filter((id) => id !== idDocumento);
    controle.setValue(marcado ? [...outros, idDocumento] : outros);
    controle.markAsTouched();
  }

  /** Mensagem de erro do comentário, só depois que o campo foi tocado. */
  erroComentario(): string | null {
    const controle = this.reenvioForm.controls.comentarios;
    if (!controle.touched || controle.valid) {
      return null;
    }
    if (controle.hasError('required') || controle.hasError('pattern')) {
      return 'Informe um comentário.';
    }
    return `O comentário deve ter no mínimo ${COMENTARIO_MINIMO} caracteres.`;
  }

  /**
   * Reprova os documentos marcados numa chamada só (cada um como `REPROVADO`, com o mesmo
   * comentário como motivo) e recarrega a solicitação. O modal fecha assim que o envio começa, para
   * o toast (de sucesso ou de erro) não ficar escondido atrás dele.
   */
  enviarReenvio(): void {
    const idSolicitacao = this.route.snapshot.paramMap.get('id');

    if (this.reenvioForm.invalid || !idSolicitacao) {
      this.reenvioForm.markAllAsTouched();
      return;
    }

    const { documentos, comentarios } = this.reenvioForm.getRawValue();
    const motivo = comentarios.trim();
    const reprovados: AvaliacaoDocumentoRequestDto[] = documentos.map((idDocumento) => ({
      idDocumento,
      resultado: 'REPROVADO',
      motivo,
    }));
    this.reenvioAberto.set(false);
    this.loaderService.showLoader();

    this.institutoPrevidenciarioService
      .avaliarDocumentos(idSolicitacao, reprovados)
      .pipe(
        take(1),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: () => {
          this.toastService.show('Pedido de reenvio enviado ao participante.', 'success');
          this.carregarSolicitacao(idSolicitacao);
        },
        error: () => this.toastService.show('Não foi possível enviar o pedido de reenvio.', 'error'),
      });
  }

  private carregarSolicitacao(id: string): void {
    this.loaderService.showLoader();

    this.institutoPrevidenciarioService
      .obterSolicitacao(id)
      .pipe(
        take(1),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: (solicitacao) => this.solicitacaoSignal.set(solicitacao),
        error: () => {
          this.naoEncontrada.set(true);
          this.toastService.show('Não foi possível carregar a solicitação.', 'error');
        },
      });
  }
}
